import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaPencilAlt, FaRegTrashAlt, FaEye } from "react-icons/fa";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import {
  fetchCategories,
  fetchCategoryById,
  deleteCategory,
  clearError,
  clearSuccess,
} from "../../redux/slices/categorySlice";
import {
  fetchSubCategoriesByCategoryId,
  deleteSubCategory,
  clearError as clearSubCategoryError,
  clearSuccess as clearSubCategorySuccess,
} from "../../redux/slices/subCategorySlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CategoryModal from "./CategoryModal";
import SubCategoryModal from "./SubCategoryModal";
import InfoPill from "../Common/InfoPill";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
const CategoryDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);

  // Use refs to track if we've already shown toasts for current messages
  const lastErrorRef = useRef(null);
  const lastSuccessRef = useRef(null);
  const lastSubCategoryErrorRef = useRef(null);
  const lastSubCategorySuccessRef = useRef(null);

  const { currentCategory, isLoading, error, success, isDeleting, isUpdating } =
    useSelector((state) => state.categories);

  const {
    subCategories = [],
    isLoading: isSubCategoriesLoading,
    error: subCategoryError,
    success: subCategorySuccess,
    isDeleting: isSubCategoryDeleting,
    isUpdating: isSubCategoryUpdating,
  } = useSelector((state) => state.subCategories);

  const { categories = [] } = useSelector((state) => state.categories);

  useEffect(() => {
    if (id) {
      dispatch(fetchCategoryById(id));
      dispatch(fetchSubCategoriesByCategoryId(id));
      // Only fetch categories if not already loaded
      if (categories.length === 0) {
        dispatch(fetchCategories());
      }
    }
    // Clear any existing messages when component mounts
    dispatch(clearError());
    dispatch(clearSuccess());
    dispatch(clearSubCategoryError());
    dispatch(clearSubCategorySuccess());
    // Reset refs on mount to allow new toasts
    lastErrorRef.current = null;
    lastSuccessRef.current = null;
    lastSubCategoryErrorRef.current = null;
    lastSubCategorySuccessRef.current = null;
  }, [dispatch, id, categories.length]);

  // Clear messages and show toasts
  useEffect(() => {
    if (error) {
      toast.error(error);
      // Clear error immediately after showing toast
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      // Clear success immediately after showing toast
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Clear subcategory messages and show toasts
  useEffect(() => {
    if (subCategoryError) {
      toast.error(subCategoryError);
      // Clear error immediately after showing toast
      dispatch(clearSubCategoryError());
    }
  }, [subCategoryError, dispatch]);

  useEffect(() => {
    if (subCategorySuccess) {
      toast.success(subCategorySuccess);
      // Clear success immediately after showing toast
      dispatch(clearSubCategorySuccess());
    }
  }, [subCategorySuccess, dispatch]);

  // Cleanup: Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      dispatch(clearSubCategoryError());
      dispatch(clearSubCategorySuccess());
      lastErrorRef.current = null;
      lastSuccessRef.current = null;
      lastSubCategoryErrorRef.current = null;
      lastSubCategorySuccessRef.current = null;
    };
  }, [dispatch]);

  const handleEditCategory = (categoryId) => {
    setEditingCategory(categoryId);
    setShowModal(true);
  };

  const handleDeleteCategory = () => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      dispatch(deleteCategory(id));
      setTimeout(() => {
        navigate("/category");
      }, 1000);
    }
  };

  const handleBack = () => {
    navigate("/category");
  };

  // Subcategory handlers
  const handleCreateSubCategory = () => {
    console.log("Creating subcategory for category ID:", id);
    setEditingSubCategory(null);
    setShowSubCategoryModal(true);
  };

  const handleEditSubCategory = (subCategoryId) => {
    setEditingSubCategory(subCategoryId);
    setShowSubCategoryModal(true);
  };

  const handleDeleteSubCategory = async (subCategoryId, subCategoryName) => {
    if (
      window.confirm(
        `Are you sure you want to delete the subcategory "${subCategoryName}"? This action cannot be undone.`
      )
    ) {
      try {
        await dispatch(deleteSubCategory(subCategoryId)).unwrap();
        // Refresh subcategories list after successful deletion
        dispatch(fetchSubCategoriesByCategoryId(id));
      } catch (error) {
        console.error("Failed to delete subcategory:", error);
      }
    }
  };

  const handleViewSubCategory = (subCategoryId) => {
    navigate(`/sub-category/details/${subCategoryId}`);
  };

  const handleCloseSubCategoryModal = () => {
    setShowSubCategoryModal(false);
    setEditingSubCategory(null);
    // Delay refetch to prevent flickering - Redux state is already updated
    // Only refetch to ensure data consistency (especially for category-filtered view)
    setTimeout(() => {
      dispatch(fetchSubCategoriesByCategoryId(id));
    }, 300);
  };

  if (isLoading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!currentCategory) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Category not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Categories
          </Button>
        </div>
      </Container>
    );
  }

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    // Refresh categories list after modal closes
    dispatch(fetchCategories());
  };

  // Subcategory table headers
  const subCategoryHeaders = [
    {
      prop: "name",
      title: "Subcategory Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <span
          className="text-decoration-none fw-bold text-primary"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleViewSubCategory(row.id);
          }}
        >
          {row.name}
        </span>
      ),
    },
    {
      prop: "image_url",
      title: "Image",
      cell: (row) => (
        <div className="subcategory-image" style={{ position: "relative" }}>
          <img
            src={row.image_url || "/not-img-found.png"} // ✅ fallback if image_url is missing
            alt={row.name}
            style={{
              width: "40px",
              height: "40px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
            onError={(e) => {
              e.target.onerror = null; // prevent loop
              e.target.src = "/not-img-found.png"; // ✅ fallback image if load fails
            }}
          />

          {/* Optional: Show placeholder text if even fallback fails */}
          <div
            className="placeholder-image"
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: "#f0f0f0",
              display: "none", // hidden by default, shown only if image fails totally
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontSize: "10px",
              color: "#666",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          ></div>
        </div>
      ),
    },
    {
      prop: "description",
      title: "Description",
      cell: (row) => (
        <div
          style={{
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={row.description}
        >
          {row.description || "No description"}
        </div>
      ),
    },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={row.status === "Active" ? "success" : "secondary"}>
          {row.status}
        </Badge>
      ),
    },
    {
      prop: "created_at",
      title: "Created Date",
      isSortable: true,
      cell: (row) => formatDateToDDMMYYYY(row.created_at) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => handleEditSubCategory(row.id)}
            disabled={isSubCategoryUpdating || isSubCategoryDeleting}
          >
            <FaPencilAlt />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteSubCategory(row.id, row.name)}
            disabled={isSubCategoryUpdating || isSubCategoryDeleting}
          >
            {/* {isSubCategoryDeleting ? "Deleting..." : "Delete"} */}
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  // Debug logging
  console.log(
    "CategoryDetails render - showSubCategoryModal:",
    showSubCategoryModal
  );
  console.log(
    "CategoryDetails render - editingSubCategory:",
    editingSubCategory
  );
  console.log("CategoryDetails render - category ID:", id);

  return (
    <Container className="view-form">
      <CategoryModal
        show={showModal}
        handleClose={handleCloseModal}
        editId={editingCategory}
      />
      <SubCategoryModal
        show={showSubCategoryModal}
        handleClose={handleCloseSubCategoryModal}
        editId={editingSubCategory}
        categoryId={id}
      />
      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">Category Details</h5>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                <i className="bi bi-arrow-left-short  fs-3"></i>
              </Button>
              <Button
                size="sm"
                variant="outline-warning"
                onClick={() => handleEditCategory(currentCategory?.id)}
                disabled={isUpdating || isDeleting}
              >
                <FaPencilAlt />
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() =>
                  handleDeleteCategory(
                    currentCategory?.id,
                    currentCategory?.name
                  )
                }
                disabled={isUpdating || isDeleting}
              >
                {/* {isDeleting ? "Deleting..." : "Delete"} */}
                <FaRegTrashAlt />
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              {/* Category Information Section */}
              <Row className="my-2 view-form">
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Category Name</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentCategory?.name || "-"}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Status</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    <Badge
                      bg={
                        currentCategory?.status === "Active"
                          ? "success"
                          : "secondary"
                      }
                    >
                      {currentCategory?.status || "-"}
                    </Badge>
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Created Date</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentCategory?.created_at
                      ? formatDateToDDMMYYYY(currentCategory.created_at)
                      : "-"}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Last Updated</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentCategory?.updated_at
                      ? formatDateToDDMMYYYY(currentCategory.updated_at)
                      : "-"}
                  </p>
                </Col>
              </Row>

              {/* Description Section */}
              <Row className="mx-3 my-2 view-form">
                <Col lg={12} className="my-2">
                  <label className="fw-bold">Description</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                      minHeight: "40px",
                    }}
                  >
                    {currentCategory?.description || "No description provided"}
                  </p>
                </Col>
              </Row>

              {/* Category Image Section */}
              <Row className="mx-3 my-2 view-form">
                <Col lg={12} className="my-2">
                  <label className="fw-bold">Category Image</label>
                  <div
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "15px",
                    }}
                  >
                    {currentCategory?.image_url ? (
                      <div className="mt-2">
                        <img
                          src={currentCategory.image_url}
                          alt={currentCategory.name}
                          style={{
                            maxWidth: "100px",
                            maxHeight: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "2px solid #dee2e6",
                          }}
                          onError={(e) => {
                            console.log(
                              "Image load error:",
                              currentCategory.image_url
                            );
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "block";
                          }}
                        />
                        <div
                          className="text-muted mt-2"
                          style={{ display: "none" }}
                        >
                          <i className="fas fa-image fa-2x mb-2"></i>
                          <p>Failed to load image</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted mt-2">
                        <i className="fas fa-image fa-2x mb-2"></i>
                        <p>No image available</p>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* SubCategories Section */}
      <Row className="mt-4">
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Subcategories ({subCategories.length})
            </h5>
            <div className="d-flex gap-2">
              <Button
                variant="success"
                size="sm"
                onClick={handleCreateSubCategory}
                disabled={isSubCategoriesLoading}
              >
                <i className="fas fa-plus me-2"></i>
                Add Subcategory
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              {isSubCategoriesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading subcategories...</p>
                </div>
              ) : subCategories.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Subcategories Found</h5>
                  <p className="text-muted">
                    This category doesn't have any subcategories yet.
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleCreateSubCategory}
                    disabled={isSubCategoriesLoading}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Create First Subcategory
                  </Button>
                </div>
              ) : (
                <DatatableWrapper
                  body={subCategories}
                  headers={subCategoryHeaders}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 5,
                      options: [5, 10, 15, 20],
                    },
                  }}
                >
                  {/* Controls */}
                  <div className="d-flex align-items-center justify-content-start flex-wrap gap-2 mb-3">
                    <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
                      <Filter
                        placeholder="Search subcategories..."
                        className="w-100"
                      />
                    </div>

                    <PaginationOptions />
                    <InfoPill left="Total" right={subCategories?.length} />
                  </div>

                  {/* Table */}
                  <div className="table-responsive shadow-sm">
                    <table className="table table-striped table-bordered align-middle">
                      <TableHeader />
                      <TableBody />
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-start mt-3">
                    <Pagination classes={{
                      button: "bg-dark",
                    }} />
                  </div>
                </DatatableWrapper>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CategoryDetails;
